/**
 * ACE Core Chemistry — Technical-term glossary.
 *
 * Source: the `narration` and `body_md` fields of content/weeks/week01.json … week09.json.
 * Each entry powers (a) a speaker-icon pronunciation + quick definition shown when a novel
 * term first appears in a lesson, and (b) the end-of-course glossary index.
 *
 * `say` is a plain phonetic respelling for at-a-glance reading; the STRESSED syllable is in CAPS.
 * `ipa` is the optional International Phonetic Alphabet form. `week` is where the term is first
 * (or primarily) taught. The array is sorted alphabetically by `term`.
 */

export interface GlossaryTerm {
  term: string;        // canonical term as written, e.g. "Electronegativity"
  slug: string;        // lowercase kebab id, e.g. "electronegativity"
  say: string;         // plain phonetic respelling for quick reading (CAPS = stressed syllable)
  ipa?: string;        // optional IPA
  definition: string;  // one or two concise, accurate sentences (dictionary style)
  week?: number;       // the week (1-9) where it's first/primarily taught
}

export const GLOSSARY: GlossaryTerm[] = [
  {
    term: "Actinides",
    slug: "actinides",
    say: "AK-tih-nydes",
    ipa: "/ˈæktɪnaɪdz/",
    definition:
      "The 15 elements from actinium (89) to lawrencium (103), occupying the bottom row of the periodic table; most are man-made and radioactive (uranium is the most familiar).",
    week: 1,
  },
  {
    term: "Activity series",
    slug: "activity-series",
    say: "ak-TIV-uh-tee SEER-eez",
    definition:
      "A ranking of metals (and the halogens) by reactivity, used to predict single-replacement reactions: a more reactive element will displace a less reactive one from its compound. Also called the reactivity series.",
    week: 5,
  },
  {
    term: "Alcohol",
    slug: "alcohol",
    say: "AL-kuh-hawl",
    ipa: "/ˈælkəhɒl/",
    definition:
      "An organic compound containing a hydroxyl (-OH) functional group bonded to a carbon chain; named by replacing the alkane's -e ending with -ol, as in propanol.",
    week: 9,
  },
  {
    term: "Aldehyde",
    slug: "aldehyde",
    say: "AL-duh-hyde",
    ipa: "/ˈældɪhaɪd/",
    definition:
      "An organic compound with a carbon double-bonded to oxygen (a carbonyl) located at the end of the carbon chain; named with the suffix -al, as in butanal.",
    week: 9,
  },
  {
    term: "Alkali metals",
    slug: "alkali-metals",
    say: "AL-kuh-ly MET-uhlz",
    ipa: "/ˈælkəlaɪ ˈmɛtəlz/",
    definition:
      "The Group 1 elements (lithium through francium): soft, lightweight, silver-colored metals with one valence electron that makes them extremely reactive.",
    week: 1,
  },
  {
    term: "Alkaline earth metals",
    slug: "alkaline-earth-metals",
    say: "AL-kuh-line URTH MET-uhlz",
    ipa: "/ˈælkəlaɪn ɜːrθ ˈmɛtəlz/",
    definition:
      "The Group 2 elements (beryllium through radium): shiny, fairly hard metals found in the Earth's crust that are moderately reactive and burn with distinctive colored flames used in fireworks.",
    week: 1,
  },
  {
    term: "Alkane",
    slug: "alkane",
    say: "AL-kayn",
    ipa: "/ˈælkeɪn/",
    definition:
      "A saturated hydrocarbon containing only single carbon-carbon bonds, with the general formula CnH2n+2; named with the suffix -ane, as in methane or octane.",
    week: 9,
  },
  {
    term: "Alkene",
    slug: "alkene",
    say: "AL-keen",
    ipa: "/ˈælkiːn/",
    definition:
      "An unsaturated hydrocarbon containing at least one carbon-carbon double bond; named with the suffix -ene, with a locant giving the double bond's position.",
    week: 9,
  },
  {
    term: "Alkyne",
    slug: "alkyne",
    say: "AL-kyne",
    ipa: "/ˈælkaɪn/",
    definition:
      "An unsaturated hydrocarbon containing at least one carbon-carbon triple bond; named with the suffix -yne, with a locant giving the triple bond's position.",
    week: 9,
  },
  {
    term: "Alloy",
    slug: "alloy",
    say: "AL-oy",
    ipa: "/ˈælɔɪ/",
    definition:
      "A homogeneous mixture of a metal with one or more other elements, such as brass (copper and zinc) or steel; it looks uniform even though it contains several ingredients.",
    week: 1,
  },
  {
    term: "Ammonium",
    slug: "ammonium",
    say: "uh-MOH-nee-um",
    ipa: "/əˈmoʊniəm/",
    definition:
      "The polyatomic cation NH4+ — the one common positively charged polyatomic ion, formed when ammonia gains a hydrogen ion.",
    week: 4,
  },
  {
    term: "Anion",
    slug: "anion",
    say: "AN-eye-on",
    ipa: "/ˈænaɪən/",
    definition:
      "A negatively charged ion, formed when an atom gains one or more electrons; nonmetals typically become anions and take an -ide ending.",
    week: 1,
  },
  {
    term: "Aqueous",
    slug: "aqueous",
    say: "AY-kwee-us",
    ipa: "/ˈeɪkwiəs/",
    definition:
      "Dissolved in water; written as the state symbol (aq) in chemical equations, as opposed to solid (s), liquid (l), or gas (g).",
    week: 5,
  },
  {
    term: "Atom",
    slug: "atom",
    say: "AT-um",
    ipa: "/ˈætəm/",
    definition:
      "The smallest piece of an element that still retains the properties of that element; for chemistry's purposes it is the basic, indivisible building block of matter.",
    week: 1,
  },
  {
    term: "Atomic mass",
    slug: "atomic-mass",
    say: "uh-TOM-ik MASS",
    ipa: "/əˈtɒmɪk mæs/",
    definition:
      "The mass of an element's atoms, printed below the symbol on the periodic table and measured in atomic mass units (amu); it is the weighted average of the element's naturally occurring isotopes.",
    week: 1,
  },
  {
    term: "Atomic mass unit",
    slug: "atomic-mass-unit",
    say: "uh-TOM-ik MASS YOO-nit",
    definition:
      "The unit (amu) used to express the tiny masses of atoms and subatomic particles; one proton or one neutron has a mass of about 1 amu.",
    week: 1,
  },
  {
    term: "Atomic number",
    slug: "atomic-number",
    say: "uh-TOM-ik NUM-ber",
    ipa: "/əˈtɒmɪk ˈnʌmbər/",
    definition:
      "The number of protons in an atom's nucleus; it uniquely identifies the element and is the value (Z) that orders the periodic table.",
    week: 1,
  },
  {
    term: "Aufbau principle",
    slug: "aufbau-principle",
    say: "OWF-bow PRIN-suh-pul",
    ipa: "/ˈaʊfbaʊ ˈprɪnsəpəl/",
    definition:
      "The rule that electrons fill the lowest-energy sublevels first when building an electron configuration; it produces the characteristic fill order (1s, 2s, 2p, 3s, ...).",
    week: 2,
  },
  {
    term: "Avogadro's number",
    slug: "avogadros-number",
    say: "av-uh-GOD-rohz NUM-ber",
    ipa: "/ˌævəˈɡɑːdroʊz ˈnʌmbər/",
    definition:
      "The number of particles in one mole, 6.022 × 10^23; it bridges the count of atoms, molecules, or formula units to the macroscopic amount we can weigh.",
    week: 6,
  },
  {
    term: "Base",
    slug: "base",
    say: "BAYSS",
    ipa: "/beɪs/",
    definition:
      "A substance that produces hydroxide ions (OH-) in water; bases feel slippery, taste bitter, and neutralize acids. Strong bases include the Group 1 and 2 hydroxides.",
    week: 8,
  },
  {
    term: "Bent",
    slug: "bent",
    say: "BENT",
    definition:
      "A VSEPR molecular shape in which two bonded atoms sit at an angle around a central atom that also carries lone pairs, as in water; the bond angle is less than the ideal straight line.",
    week: 3,
  },
  {
    term: "Bohr model",
    slug: "bohr-model",
    say: "BORE MOD-uhl",
    ipa: "/bɔːr ˈmɒdəl/",
    definition:
      "Niels Bohr's atomic model in which electrons occupy fixed energy levels (shells) around the nucleus and emit light when they drop from a higher level to a lower one.",
    week: 2,
  },
  {
    term: "Bond angle",
    slug: "bond-angle",
    say: "BOND ANG-gul",
    definition:
      "The angle formed between two bonds meeting at a central atom; VSEPR theory predicts characteristic angles such as 109.5° (tetrahedral) or 180° (linear).",
    week: 3,
  },
  {
    term: "Carboxylic acid",
    slug: "carboxylic-acid",
    say: "kar-bok-SIL-ik AS-id",
    ipa: "/ˌkɑːrbɒkˈsɪlɪk ˈæsɪd/",
    definition:
      "An organic compound containing the carboxyl group (-COOH), a carbon bonded to both a double-bonded oxygen and a hydroxyl; named with the suffix -oic acid, as in propanoic acid.",
    week: 9,
  },
  {
    term: "Catalyst",
    slug: "catalyst",
    say: "KAT-uh-list",
    ipa: "/ˈkætəlɪst/",
    definition:
      "A substance that speeds up a chemical reaction without being consumed by it; in an equation it is written above the reaction arrow rather than as a reactant.",
    week: 5,
  },
  {
    term: "Cation",
    slug: "cation",
    say: "KAT-eye-on",
    ipa: "/ˈkætaɪən/",
    definition:
      "A positively charged ion, formed when an atom loses one or more electrons; metals typically become cations.",
    week: 1,
  },
  {
    term: "Chemical change",
    slug: "chemical-change",
    say: "KEM-ih-kul CHAYNJ",
    definition:
      "A change that produces one or more new substances with different properties, such as burning or rusting; it cannot be reversed by simple physical means.",
    week: 1,
  },
  {
    term: "Chemical formula",
    slug: "chemical-formula",
    say: "KEM-ih-kul FOR-myuh-luh",
    definition:
      "A shorthand using element symbols and subscripts to show exactly which elements, and how many atoms of each, make up one unit of a compound, as in H2O.",
    week: 1,
  },
  {
    term: "Coefficient",
    slug: "coefficient",
    say: "koh-uh-FISH-unt",
    ipa: "/ˌkoʊɪˈfɪʃənt/",
    definition:
      "A whole number placed in front of a formula in a chemical equation to balance it; it multiplies every atom in that formula, unlike a subscript, which affects only one element.",
    week: 5,
  },
  {
    term: "Combustion",
    slug: "combustion",
    say: "kum-BUS-chun",
    ipa: "/kəmˈbʌstʃən/",
    definition:
      "A reaction in which a hydrocarbon (or other fuel) reacts with oxygen to release energy, producing carbon dioxide and water as products.",
    week: 5,
  },
  {
    term: "Compound",
    slug: "compound",
    say: "KOM-pownd",
    ipa: "/ˈkɒmpaʊnd/",
    definition:
      "A pure substance made of two or more elements chemically combined in a fixed proportion, with its own chemical formula and properties distinct from its elements.",
    week: 1,
  },
  {
    term: "Concentration",
    slug: "concentration",
    say: "kon-sen-TRAY-shun",
    ipa: "/ˌkɒnsɛnˈtreɪʃən/",
    definition:
      "A measure of how much solute is dissolved in a given amount of solution; molarity (moles per liter) is the most common way to express it.",
    week: 7,
  },
  {
    term: "Condensation",
    slug: "condensation",
    say: "kon-den-SAY-shun",
    ipa: "/ˌkɒndɛnˈseɪʃən/",
    definition:
      "The change of state from a gas to a liquid, the reverse of evaporation; it occurs as particles lose energy and slow down.",
    week: 1,
  },
  {
    term: "Covalent bond",
    slug: "covalent-bond",
    say: "koh-VAY-lunt BOND",
    ipa: "/koʊˈveɪlənt bɒnd/",
    definition:
      "A bond in which two atoms (usually nonmetals) share one or more pairs of electrons so that each can reach a stable octet.",
    week: 3,
  },
  {
    term: "Crossover rule",
    slug: "crossover-rule",
    say: "KRAWS-oh-ver ROOL",
    definition:
      "A shortcut for writing ionic formulas: the numerical value of each ion's charge becomes the subscript of the other ion, then the subscripts are reduced to lowest whole numbers.",
    week: 4,
  },
  {
    term: "Cycloalkane",
    slug: "cycloalkane",
    say: "SY-kloh-AL-kayn",
    ipa: "/ˌsaɪkloʊˈælkeɪn/",
    definition:
      "An alkane whose carbon atoms form a closed ring rather than a chain; named by adding the prefix cyclo- to the alkane name, as in cyclobutane.",
    week: 9,
  },
  {
    term: "Decomposition",
    slug: "decomposition",
    say: "dee-kom-puh-ZISH-un",
    ipa: "/ˌdiːkɒmpəˈzɪʃən/",
    definition:
      "A reaction in which a single compound breaks apart into two or more simpler products, often when heated; the opposite of synthesis.",
    week: 5,
  },
  {
    term: "Density",
    slug: "density",
    say: "DEN-suh-tee",
    ipa: "/ˈdɛnsɪti/",
    definition:
      "The amount of mass packed into a given volume, calculated as mass divided by volume and usually reported in grams per milliliter or grams per liter.",
    week: 1,
  },
  {
    term: "Deposition",
    slug: "deposition",
    say: "dep-uh-ZISH-un",
    ipa: "/ˌdɛpəˈzɪʃən/",
    definition:
      "The change of state directly from a gas to a solid, skipping the liquid phase; the reverse of sublimation, as when frost forms on a cold surface.",
    week: 1,
  },
  {
    term: "Diatomic",
    slug: "diatomic",
    say: "dy-uh-TOM-ik",
    ipa: "/ˌdaɪəˈtɒmɪk/",
    definition:
      "Describing an element that occurs naturally as a molecule of two bonded atoms; the seven diatomic elements include H2, N2, O2, and the halogens.",
    week: 4,
  },
  {
    term: "Dilution",
    slug: "dilution",
    say: "dy-LOO-shun",
    ipa: "/daɪˈluːʃən/",
    definition:
      "The process of adding more solvent (usually water) to a solution to lower its concentration; a diluted solution holds less solute per unit volume.",
    week: 7,
  },
  {
    term: "Dissociation",
    slug: "dissociation",
    say: "dih-soh-see-AY-shun",
    ipa: "/dɪˌsoʊsiˈeɪʃən/",
    definition:
      "The separation of a soluble ionic compound into its free ions when it dissolves in water, as when NaCl breaks into Na+ and Cl- ions.",
    week: 7,
  },
  {
    term: "Double bond",
    slug: "double-bond",
    say: "DUB-ul BOND",
    definition:
      "A covalent bond in which two atoms share two pairs of electrons (four electrons total), as found between the carbons of an alkene or in O2.",
    week: 3,
  },
  {
    term: "Double replacement",
    slug: "double-replacement",
    say: "DUB-ul ree-PLAYSS-munt",
    definition:
      "A reaction in which two compounds exchange partner ions to form two new compounds; it includes precipitation reactions and acid-base neutralizations.",
    week: 5,
  },
  {
    term: "Ductile",
    slug: "ductile",
    say: "DUK-tul",
    ipa: "/ˈdʌktaɪl/",
    definition:
      "Able to be drawn out into a thin wire without breaking; a typical property of metals.",
    week: 1,
  },
  {
    term: "Electron",
    slug: "electron",
    say: "ee-LEK-tron",
    ipa: "/ɪˈlɛktrɒn/",
    definition:
      "A negatively charged subatomic particle that occupies the space around the nucleus; its arrangement determines how an atom bonds and behaves chemically.",
    week: 2,
  },
  {
    term: "Electron configuration",
    slug: "electron-configuration",
    say: "ee-LEK-tron kun-fig-yuh-RAY-shun",
    definition:
      "A notation showing how an atom's electrons are distributed among its sublevels, such as 1s² 2s² 2p⁶; it acts as the atom's identifying fingerprint.",
    week: 2,
  },
  {
    term: "Electronegativity",
    slug: "electronegativity",
    say: "ee-lek-troh-NEG-uh-TIV-uh-tee",
    ipa: "/ɪˌlɛktroʊnɛɡəˈtɪvɪti/",
    definition:
      "A measure of how strongly an atom attracts the shared electrons in a bond; the difference in electronegativity between two atoms determines whether a bond is nonpolar, polar covalent, or ionic.",
    week: 3,
  },
  {
    term: "Element",
    slug: "element",
    say: "EL-uh-munt",
    ipa: "/ˈɛləmənt/",
    definition:
      "A pure substance that cannot be broken down into simpler substances by chemical means; the 118 known elements are organized on the periodic table.",
    week: 1,
  },
  {
    term: "Empirical formula",
    slug: "empirical-formula",
    say: "em-PEER-ih-kul FOR-myuh-luh",
    ipa: "/ɛmˈpɪrɪkəl ˈfɔːrmjʊlə/",
    definition:
      "The simplest whole-number ratio of the elements in a compound, such as CH2O; it may differ from the molecular formula, which gives the actual atom counts.",
    week: 6,
  },
  {
    term: "Endpoint",
    slug: "endpoint",
    say: "END-poynt",
    definition:
      "The point in a titration where the indicator changes color, signaling that the reaction is complete; it is used to mark the equivalence point.",
    week: 8,
  },
  {
    term: "Equilibrium",
    slug: "equilibrium",
    say: "ee-kwuh-LIB-ree-um",
    ipa: "/ˌiːkwɪˈlɪbriəm/",
    definition:
      "The state of a reversible reaction in which the forward and reverse processes occur at equal rates, so concentrations stay constant; weak acids and bases only partly dissociate and sit at equilibrium.",
    week: 8,
  },
  {
    term: "Equivalence point",
    slug: "equivalence-point",
    say: "ee-KWIV-uh-lunss POYNT",
    ipa: "/ɪˈkwɪvələns pɔɪnt/",
    definition:
      "The moment in a titration when the moles of acid and base have reacted in their exact stoichiometric ratio, leaving neither in excess.",
    week: 8,
  },
  {
    term: "Ester",
    slug: "ester",
    say: "ESS-ter",
    ipa: "/ˈɛstər/",
    definition:
      "An organic compound formed from a carboxylic acid and an alcohol, containing a carbon double-bonded to one oxygen and single-bonded to another; named as an alkyl ...oate, as in propyl pentanoate.",
    week: 9,
  },
  {
    term: "Ether",
    slug: "ether",
    say: "EE-ther",
    ipa: "/ˈiːθər/",
    definition:
      "An organic compound in which an oxygen atom bridges two carbon chains; named with an -oxy- prefix, as in methoxypropane.",
    week: 9,
  },
  {
    term: "Evaporation",
    slug: "evaporation",
    say: "ee-vap-uh-RAY-shun",
    ipa: "/ɪˌvæpəˈreɪʃən/",
    definition:
      "The change of state from a liquid to a gas as particles gain enough energy to escape the surface.",
    week: 1,
  },
  {
    term: "Excess reactant",
    slug: "excess-reactant",
    say: "ek-SESS ree-AK-tunt",
    definition:
      "The reactant that remains left over after a reaction stops because the limiting reactant has been completely used up.",
    week: 7,
  },
  {
    term: "Formula unit",
    slug: "formula-unit",
    say: "FOR-myuh-luh YOO-nit",
    definition:
      "The smallest whole-number ratio of ions in an ionic compound, as represented by its formula; the ionic-compound counterpart to a molecule.",
    week: 6,
  },
  {
    term: "Functional group",
    slug: "functional-group",
    say: "FUNK-shun-ul GROOP",
    definition:
      "A specific arrangement of atoms within an organic molecule that gives the molecule its characteristic chemistry, such as the -OH of an alcohol or the -COOH of a carboxylic acid.",
    week: 9,
  },
  {
    term: "Group",
    slug: "group",
    say: "GROOP",
    ipa: "/ɡruːp/",
    definition:
      "A vertical column of the periodic table, also called a family; the 18 groups gather elements that share similar properties and the same number of valence electrons.",
    week: 1,
  },
  {
    term: "Halogens",
    slug: "halogens",
    say: "HAL-uh-junz",
    ipa: "/ˈhælədʒənz/",
    definition:
      "The Group 17 elements (fluorine through tennessine): reactive nonmetals with seven valence electrons that readily gain one electron to form -ide anions.",
    week: 1,
  },
  {
    term: "Heterogeneous mixture",
    slug: "heterogeneous-mixture",
    say: "HET-er-oh-JEE-nee-us MIKS-cher",
    ipa: "/ˌhɛtərəˈdʒiːniəs ˈmɪkstʃər/",
    definition:
      "A mixture in which the different parts are visibly distinct, such as a salad or sand in water; you can see more than one component.",
    week: 1,
  },
  {
    term: "Homogeneous mixture",
    slug: "homogeneous-mixture",
    say: "HOH-moh-JEE-nee-us MIKS-cher",
    ipa: "/ˌhoʊməˈdʒiːniəs ˈmɪkstʃər/",
    definition:
      "A mixture that looks uniform throughout, so only one component is visible even though several are present; also called a solution, as in sweetened tea.",
    week: 1,
  },
  {
    term: "Hydrocarbon",
    slug: "hydrocarbon",
    say: "HY-droh-KAR-bun",
    ipa: "/ˈhaɪdroʊˌkɑːrbən/",
    definition:
      "An organic compound made only of hydrogen and carbon; the alkanes, alkenes, and alkynes are the three main hydrocarbon families.",
    week: 9,
  },
  {
    term: "Hydronium",
    slug: "hydronium",
    say: "hy-DROH-nee-um",
    ipa: "/haɪˈdroʊniəm/",
    definition:
      "The ion H3O+, formed when a hydrogen ion (H+) from an acid attaches to a water molecule; it is what makes a solution acidic.",
    week: 8,
  },
  {
    term: "Hydroxide",
    slug: "hydroxide",
    say: "hy-DROK-side",
    ipa: "/haɪˈdrɒksaɪd/",
    definition:
      "The polyatomic anion OH-; bases release hydroxide ions in water, and a high hydroxide concentration makes a solution basic.",
    week: 8,
  },
  {
    term: "ICE table",
    slug: "ice-table",
    say: "EYESS TAY-bul",
    definition:
      "A bookkeeping grid listing the Initial, Change, and Equilibrium amounts of each species, used to solve weak-acid and weak-base equilibrium problems for Ka or Kb.",
    week: 8,
  },
  {
    term: "Indicator",
    slug: "indicator",
    say: "IN-dih-KAY-ter",
    ipa: "/ˈɪndɪkeɪtər/",
    definition:
      "A substance, such as phenolphthalein, that changes color at a certain pH and is added to a titration to reveal when the endpoint is reached.",
    week: 8,
  },
  {
    term: "Inner transition metals",
    slug: "inner-transition-metals",
    say: "IN-er tran-ZISH-un MET-uhlz",
    definition:
      "The 30 elements pulled out into the two rows below the main table — the lanthanides and actinides — all formally belonging to Group 3.",
    week: 1,
  },
  {
    term: "Ion",
    slug: "ion",
    say: "EYE-on",
    ipa: "/ˈaɪɒn/",
    definition:
      "An atom or group of atoms that carries a net electric charge because it has gained or lost electrons; positive ions are cations and negative ions are anions.",
    week: 1,
  },
  {
    term: "Ionic bond",
    slug: "ionic-bond",
    say: "eye-ON-ik BOND",
    ipa: "/aɪˈɒnɪk bɒnd/",
    definition:
      "A bond formed when one atom transfers electrons to another, producing oppositely charged ions that attract; it typically joins a metal with a nonmetal.",
    week: 3,
  },
  {
    term: "Ionization energy",
    slug: "ionization-energy",
    say: "eye-on-ih-ZAY-shun EN-er-jee",
    ipa: "/ˌaɪənɪˈzeɪʃən ˈɛnərdʒi/",
    definition:
      "The energy required to remove an electron from an atom; it generally increases left to right across a period and decreases down a group.",
    week: 3,
  },
  {
    term: "Isoelectronic",
    slug: "isoelectronic",
    say: "EYE-soh-ee-lek-TRON-ik",
    ipa: "/ˌaɪsoʊɪlɛkˈtrɒnɪk/",
    definition:
      "Describing two species (often an ion and a noble gas) that have the same number of electrons and therefore the same electron configuration.",
    week: 3,
  },
  {
    term: "Isotopes",
    slug: "isotopes",
    say: "EYE-suh-tohps",
    ipa: "/ˈaɪsətoʊps/",
    definition:
      "Atoms of the same element that have the same number of protons but different numbers of neutrons, and therefore different mass numbers.",
    week: 2,
  },
  {
    term: "Ka",
    slug: "ka",
    say: "KAY-AY",
    definition:
      "The acid dissociation constant, a number that measures the strength of a weak acid; a larger Ka means the acid dissociates more fully in water.",
    week: 8,
  },
  {
    term: "Kb",
    slug: "kb",
    say: "KAY-BEE",
    definition:
      "The base dissociation constant, a number that measures the strength of a weak base; a larger Kb means more hydroxide is produced in water.",
    week: 8,
  },
  {
    term: "Ketone",
    slug: "ketone",
    say: "KEE-tohn",
    ipa: "/ˈkiːtoʊn/",
    definition:
      "An organic compound with a carbonyl group (carbon double-bonded to oxygen) located in the middle of the carbon chain; named with the suffix -one, as in hexanone.",
    week: 9,
  },
  {
    term: "Kw",
    slug: "kw",
    say: "KAY-DUB-ul-yoo",
    definition:
      "The ion-product constant for water, equal to 1.0 × 10^-14 at 25 °C; it links the hydronium and hydroxide concentrations so that pH + pOH = 14.",
    week: 8,
  },
  {
    term: "Lanthanides",
    slug: "lanthanides",
    say: "LAN-thuh-nydes",
    ipa: "/ˈlænθənaɪdz/",
    definition:
      "The 15 elements from lanthanum (57) to lutetium (71); together with scandium and yttrium they make up the rare earth metals used in modern electronics.",
    week: 1,
  },
  {
    term: "Law of conservation of matter",
    slug: "law-of-conservation-of-matter",
    say: "LAW uv kon-ser-VAY-shun uv MAT-er",
    definition:
      "The principle that matter is neither created nor destroyed in a chemical change, so the total mass of the reactants equals the total mass of the products.",
    week: 1,
  },
  {
    term: "Law of definite proportions",
    slug: "law-of-definite-proportions",
    say: "LAW uv DEF-uh-nit pruh-POR-shunz",
    definition:
      "The principle that a given compound always contains the same elements in the same fixed ratio by mass, no matter how it is prepared.",
    week: 1,
  },
  {
    term: "Lewis structure",
    slug: "lewis-structure",
    say: "LOO-iss STRUK-cher",
    ipa: "/ˈluːɪs ˈstrʌktʃər/",
    definition:
      "A diagram that shows how atoms are bonded and where lone pairs sit, using dots for unshared electrons and lines for shared (bonding) pairs.",
    week: 3,
  },
  {
    term: "Limiting reactant",
    slug: "limiting-reactant",
    say: "LIM-it-ing ree-AK-tunt",
    definition:
      "The reactant that runs out first and thereby caps how much product can form; the leftover reactant is the excess reactant.",
    week: 7,
  },
  {
    term: "Linear",
    slug: "linear",
    say: "LIN-ee-er",
    ipa: "/ˈlɪniər/",
    definition:
      "A VSEPR molecular shape in which atoms lie in a straight line around the central atom, giving a bond angle of 180°, as in carbon dioxide.",
    week: 3,
  },
  {
    term: "Locant",
    slug: "locant",
    say: "LOH-kunt",
    ipa: "/ˈloʊkənt/",
    definition:
      "The position number placed in an organic compound's name to show where a branch, multiple bond, or functional group sits along the main carbon chain.",
    week: 9,
  },
  {
    term: "Lone pair",
    slug: "lone-pair",
    say: "LOHN PAIR",
    definition:
      "A pair of valence electrons that belongs to one atom and is not shared in a bond; lone pairs influence a molecule's shape in VSEPR theory.",
    week: 3,
  },
  {
    term: "Luster",
    slug: "luster",
    say: "LUS-ter",
    ipa: "/ˈlʌstər/",
    definition:
      "The shiny, reflective appearance characteristic of metals; metalloids also show a metallic luster.",
    week: 1,
  },
  {
    term: "Malleable",
    slug: "malleable",
    say: "MAL-ee-uh-bul",
    ipa: "/ˈmæliəbəl/",
    definition:
      "Able to be hammered or pressed into thin sheets or new shapes without breaking; a typical property of metals.",
    week: 1,
  },
  {
    term: "Matter",
    slug: "matter",
    say: "MAT-er",
    ipa: "/ˈmætər/",
    definition:
      "Anything that has mass and takes up space; everything physical is matter, with energy being its only alternative.",
    week: 1,
  },
  {
    term: "Metalloid",
    slug: "metalloid",
    say: "MET-uh-loyd",
    ipa: "/ˈmɛtəlɔɪd/",
    definition:
      "An element along the staircase boundary between metals and nonmetals (such as silicon or arsenic) that shares properties of both and often acts as a semiconductor. Also called a semi-metal.",
    week: 1,
  },
  {
    term: "Mixture",
    slug: "mixture",
    say: "MIKS-cher",
    ipa: "/ˈmɪkstʃər/",
    definition:
      "A physical blend of two or more substances that are not chemically combined and can vary in proportion; classified as homogeneous or heterogeneous by what the eye can see.",
    week: 1,
  },
  {
    term: "Molar mass",
    slug: "molar-mass",
    say: "MOH-ler MASS",
    ipa: "/ˈmoʊlər mæs/",
    definition:
      "The mass of one mole of a substance, in grams, found by adding up the atomic masses in its formula; it converts between mass and moles.",
    week: 6,
  },
  {
    term: "Molar volume",
    slug: "molar-volume",
    say: "MOH-ler VOL-yoom",
    definition:
      "The volume occupied by one mole of any gas at standard temperature and pressure, equal to 22.4 liters.",
    week: 6,
  },
  {
    term: "Molarity",
    slug: "molarity",
    say: "moh-LAIR-uh-tee",
    ipa: "/moʊˈlærɪti/",
    definition:
      "A measure of concentration equal to the number of moles of solute dissolved per liter of solution, with units of mol/L (M).",
    week: 7,
  },
  {
    term: "Mole",
    slug: "mole",
    say: "MOHL",
    ipa: "/moʊl/",
    definition:
      "The chemist's counting unit for particles: one mole is 6.022 × 10^23 atoms, molecules, or formula units — the amount whose mass in grams equals the substance's formula mass.",
    week: 6,
  },
  {
    term: "Mole ratio",
    slug: "mole-ratio",
    say: "MOHL RAY-shee-oh",
    definition:
      "The ratio of the coefficients of two substances in a balanced equation; it is the bridge used to convert moles of one substance into moles of another.",
    week: 7,
  },
  {
    term: "Molecular formula",
    slug: "molecular-formula",
    say: "muh-LEK-yuh-ler FOR-myuh-luh",
    ipa: "/məˈlɛkjʊlər ˈfɔːrmjʊlə/",
    definition:
      "A formula giving the actual number of atoms of each element in one molecule of a compound; it is a whole-number multiple of the empirical formula.",
    week: 6,
  },
  {
    term: "Molecule",
    slug: "molecule",
    say: "MOL-uh-kyool",
    ipa: "/ˈmɒlɪkjuːl/",
    definition:
      "A particle made of two or more atoms held together by covalent bonds; molecules are the basic units of most covalent compounds.",
    week: 1,
  },
  {
    term: "Monatomic ion",
    slug: "monatomic-ion",
    say: "MON-uh-TOM-ik EYE-on",
    ipa: "/ˌmɒnəˈtɒmɪk ˈaɪɒn/",
    definition:
      "An ion consisting of a single atom that carries a charge, such as Na+ or Cl-; contrasted with polyatomic ions, which contain several atoms.",
    week: 4,
  },
  {
    term: "Monoprotic acid",
    slug: "monoprotic-acid",
    say: "MON-oh-PROH-tik AS-id",
    definition:
      "An acid that can donate exactly one hydrogen ion per molecule, such as HCl; contrasted with diprotic and triprotic acids, which donate two or three.",
    week: 8,
  },
  {
    term: "Net ionic equation",
    slug: "net-ionic-equation",
    say: "NET eye-ON-ik ee-KWAY-zhun",
    definition:
      "An equation that shows only the ions and compounds that actually take part in a reaction, with spectator ions removed; it captures the real chemical change.",
    week: 7,
  },
  {
    term: "Neutralization",
    slug: "neutralization",
    say: "NOO-truh-lih-ZAY-shun",
    ipa: "/ˌnuːtrəlaɪˈzeɪʃən/",
    definition:
      "The double-replacement reaction between an acid and a base that produces water and a salt, canceling their acidic and basic properties.",
    week: 5,
  },
  {
    term: "Neutron",
    slug: "neutron",
    say: "NOO-tron",
    ipa: "/ˈnuːtrɒn/",
    definition:
      "A subatomic particle with no electric charge and a mass of about 1 amu, found in the nucleus alongside protons.",
    week: 2,
  },
  {
    term: "Noble gases",
    slug: "noble-gases",
    say: "NOH-bul GAS-ez",
    ipa: "/ˈnoʊbəl ˈɡæsɪz/",
    definition:
      "The Group 18 elements (helium through radon): colorless, extremely stable gases with a full outer shell, so they rarely react; their stability is the octet every other atom seeks.",
    week: 1,
  },
  {
    term: "Noble-gas shorthand",
    slug: "noble-gas-shorthand",
    say: "NOH-bul GAS SHORT-hand",
    definition:
      "A condensed electron configuration that replaces the inner (core) electrons with the symbol of the preceding noble gas in brackets, as in [Ne] 3s² for magnesium.",
    week: 2,
  },
  {
    term: "Nomenclature",
    slug: "nomenclature",
    say: "NOH-men-klay-cher",
    ipa: "/ˈnoʊmənkleɪtʃər/",
    definition:
      "The systematic set of rules for naming chemical compounds and ions so that each name corresponds to exactly one formula.",
    week: 4,
  },
  {
    term: "Nonmetals",
    slug: "nonmetals",
    say: "non-MET-uhlz",
    ipa: "/nɒnˈmɛtəlz/",
    definition:
      "Elements that lack metallic properties — they are poor conductors and are gases or brittle solids; they tend to gain electrons and form anions.",
    week: 1,
  },
  {
    term: "Nonpolar covalent bond",
    slug: "nonpolar-covalent-bond",
    say: "non-POH-ler koh-VAY-lunt BOND",
    definition:
      "A covalent bond in which electrons are shared roughly equally because the two atoms have an electronegativity difference of about 0 to 0.4.",
    week: 3,
  },
  {
    term: "Nucleus",
    slug: "nucleus",
    say: "NOO-klee-us",
    ipa: "/ˈnuːkliəs/",
    definition:
      "The tiny, dense, positively charged center of an atom that contains the protons and neutrons and holds almost all of the atom's mass.",
    week: 2,
  },
  {
    term: "Octet",
    slug: "octet",
    say: "ok-TET",
    ipa: "/ɒkˈtɛt/",
    definition:
      "A full outer shell of eight valence electrons, the stable arrangement of the noble gases that atoms tend to achieve by gaining, losing, or sharing electrons.",
    week: 1,
  },
  {
    term: "Orbital",
    slug: "orbital",
    say: "OR-bih-tul",
    ipa: "/ˈɔːrbɪtəl/",
    definition:
      "A region of space around the nucleus where an electron is likely to be found; each orbital holds at most two electrons, and orbitals group into the s, p, d, and f sublevels.",
    week: 2,
  },
  {
    term: "Pauling scale",
    slug: "pauling-scale",
    say: "PAW-ling SKAYL",
    ipa: "/ˈpɔːlɪŋ skeɪl/",
    definition:
      "The standard numerical scale of electronegativity values devised by Linus Pauling, used to compare atoms and classify a bond as nonpolar, polar, or ionic.",
    week: 3,
  },
  {
    term: "Percent composition",
    slug: "percent-composition",
    say: "per-SENT kom-puh-ZISH-un",
    definition:
      "The percentage by mass that each element contributes to a compound, found by dividing an element's mass by the compound's total mass and multiplying by 100.",
    week: 1,
  },
  {
    term: "Period",
    slug: "period",
    say: "PEER-ee-ud",
    ipa: "/ˈpɪəriəd/",
    definition:
      "A horizontal row of the periodic table; there are seven periods, and an element's period number equals the number of its occupied electron shells.",
    week: 1,
  },
  {
    term: "Periodic table",
    slug: "periodic-table",
    say: "peer-ee-OD-ik TAY-bul",
    ipa: "/ˌpɪəriˈɒdɪk ˈteɪbəl/",
    definition:
      "The chart that organizes all known elements by increasing atomic number into periods and groups so that elements with similar properties line up in columns.",
    week: 1,
  },
  {
    term: "pH",
    slug: "ph",
    say: "PEE-AYCH",
    definition:
      "A scale from 0 to 14 measuring how acidic or basic a solution is, based on its hydronium-ion concentration; below 7 is acidic, 7 is neutral, and above 7 is basic.",
    week: 8,
  },
  {
    term: "Physical change",
    slug: "physical-change",
    say: "FIZ-ih-kul CHAYNJ",
    definition:
      "A change in the form or appearance of a substance — such as melting, dissolving, or cutting — without producing any new substance.",
    week: 1,
  },
  {
    term: "Plum pudding model",
    slug: "plum-pudding-model",
    say: "PLUM PUD-ing MOD-uhl",
    definition:
      "J.J. Thomson's early atomic model picturing the atom as a sphere of positive charge with electrons scattered throughout it like plums in a pudding.",
    week: 2,
  },
  {
    term: "pOH",
    slug: "poh",
    say: "PEE-OH-AYCH",
    definition:
      "A scale measuring how basic a solution is, based on its hydroxide-ion concentration; it is the counterpart to pH, with pH + pOH = 14.",
    week: 8,
  },
  {
    term: "Polar covalent bond",
    slug: "polar-covalent-bond",
    say: "POH-ler koh-VAY-lunt BOND",
    definition:
      "A covalent bond in which electrons are shared unequally because the atoms differ in electronegativity by about 0.4 to 2.0, giving the bond partial positive and negative ends.",
    week: 3,
  },
  {
    term: "Polyatomic ion",
    slug: "polyatomic-ion",
    say: "POL-ee-uh-TOM-ik EYE-on",
    ipa: "/ˌpɒliəˈtɒmɪk ˈaɪɒn/",
    definition:
      "A charged cluster of two or more atoms bonded together that acts as a single unit, such as sulfate (SO4 2-) or ammonium (NH4+).",
    week: 4,
  },
  {
    term: "Post-transition metals",
    slug: "post-transition-metals",
    say: "POHST tran-ZISH-un MET-uhlz",
    definition:
      "The metals just to the right of the transition metals (such as aluminum, tin, and lead): solid, dense, conductive metals that are still ductile and malleable.",
    week: 1,
  },
  {
    term: "Precipitate",
    slug: "precipitate",
    say: "pruh-SIP-ih-tut",
    ipa: "/prɪˈsɪpɪtət/",
    definition:
      "An insoluble solid that forms and drops out of solution when two solutions are mixed in a double-replacement reaction.",
    week: 7,
  },
  {
    term: "Proton",
    slug: "proton",
    say: "PROH-ton",
    ipa: "/ˈproʊtɒn/",
    definition:
      "A positively charged subatomic particle in the nucleus with a mass of about 1 amu; the number of protons (the atomic number) defines the element.",
    week: 2,
  },
  {
    term: "Pure substance",
    slug: "pure-substance",
    say: "PYOOR SUB-stunss",
    definition:
      "Matter with a fixed, uniform composition and consistent properties; the elements and compounds, as opposed to mixtures.",
    week: 1,
  },
  {
    term: "Qualitative",
    slug: "qualitative",
    say: "KWOL-ih-tay-tiv",
    ipa: "/ˈkwɒlɪteɪtɪv/",
    definition:
      "Describing an observation made about quality, such as color, odor, or texture, that does not involve a measured number.",
    week: 1,
  },
  {
    term: "Quantitative",
    slug: "quantitative",
    say: "KWON-tih-tay-tiv",
    ipa: "/ˈkwɒntɪteɪtɪv/",
    definition:
      "Describing an observation expressed as a measured number with units, such as mass, volume, or temperature.",
    week: 1,
  },
  {
    term: "Rare earth metals",
    slug: "rare-earth-metals",
    say: "RAIR URTH MET-uhlz",
    definition:
      "The 17 scarce metals — the 15 lanthanides plus scandium and yttrium — that are essential to electronics such as smartphones and make up less than 0.005% of Earth's crust.",
    week: 1,
  },
  {
    term: "Reactants",
    slug: "reactants",
    say: "ree-AK-tunts",
    ipa: "/riˈæktənts/",
    definition:
      "The starting substances in a chemical reaction, written on the left side of the arrow; they are converted into the products.",
    week: 1,
  },
  {
    term: "Saturated solution",
    slug: "saturated-solution",
    say: "SACH-uh-ray-ted suh-LOO-shun",
    definition:
      "A solution that holds the maximum amount of dissolved solute at a given temperature; any extra solute will not dissolve.",
    week: 7,
  },
  {
    term: "Semiconductor",
    slug: "semiconductor",
    say: "SEM-ee-kun-DUK-ter",
    ipa: "/ˌsɛmikənˈdʌktər/",
    definition:
      "A material, such as silicon, that conducts electricity better than an insulator but worse than a metal; metalloids are typical semiconductors.",
    week: 1,
  },
  {
    term: "Shell",
    slug: "shell",
    say: "SHEL",
    ipa: "/ʃɛl/",
    definition:
      "An energy level around the nucleus that holds electrons; shells are numbered outward from the nucleus, and the outermost shell holds the valence electrons.",
    week: 1,
  },
  {
    term: "Significant digits",
    slug: "significant-digits",
    say: "sig-NIF-ih-kunt DIJ-its",
    ipa: "/sɪɡˈnɪfɪkənt ˈdɪdʒɪts/",
    definition:
      "The digits in a measurement that carry real, reliable information; rules for them control how many digits to keep when multiplying, dividing, adding, or subtracting. Also called significant figures.",
    week: 1,
  },
  {
    term: "Single bond",
    slug: "single-bond",
    say: "SING-gul BOND",
    definition:
      "A covalent bond in which two atoms share one pair of electrons; all carbon-carbon bonds in an alkane are single bonds.",
    week: 3,
  },
  {
    term: "Single replacement",
    slug: "single-replacement",
    say: "SING-gul ree-PLAYSS-munt",
    definition:
      "A reaction in which one element replaces another in a compound; whether it proceeds is predicted by the activity (reactivity) series.",
    week: 5,
  },
  {
    term: "Solubility",
    slug: "solubility",
    say: "sol-yuh-BIL-uh-tee",
    ipa: "/ˌsɒljʊˈbɪlɪti/",
    definition:
      "The maximum amount of a solute that will dissolve in a given amount of solvent at a particular temperature, often read from a solubility curve.",
    week: 7,
  },
  {
    term: "Solute",
    slug: "solute",
    say: "SOL-yoot",
    ipa: "/ˈsɒljuːt/",
    definition:
      "The substance that is dissolved in a solution, present in the smaller amount, such as the salt in salt water.",
    week: 7,
  },
  {
    term: "Solvent",
    slug: "solvent",
    say: "SOL-vunt",
    ipa: "/ˈsɒlvənt/",
    definition:
      "The substance that does the dissolving in a solution, present in the larger amount; water is the most common solvent.",
    week: 7,
  },
  {
    term: "Spectator ion",
    slug: "spectator-ion",
    say: "SPEK-tay-ter EYE-on",
    definition:
      "An ion that appears unchanged on both sides of a reaction and takes no part in the chemical change; spectator ions are dropped to write the net ionic equation.",
    week: 7,
  },
  {
    term: "Standard temperature and pressure",
    slug: "standard-temperature-and-pressure",
    say: "STAN-derd TEM-per-uh-cher and PRESH-er",
    definition:
      "The reference conditions of 0 °C and 1 atmosphere of pressure (STP), at which one mole of any gas occupies 22.4 liters.",
    week: 6,
  },
  {
    term: "Stock system",
    slug: "stock-system",
    say: "STOK SIS-tum",
    definition:
      "The modern naming convention that shows a transition metal's charge with a Roman numeral in parentheses, as in iron(III) chloride.",
    week: 4,
  },
  {
    term: "Stoichiometry",
    slug: "stoichiometry",
    say: "STOY-kee-OM-uh-tree",
    ipa: "/ˌstɔɪkiˈɒmɪtri/",
    definition:
      "The study of the quantitative relationships between reactants and products in a chemical reaction, using mole ratios from the balanced equation.",
    week: 8,
  },
  {
    term: "Subatomic particle",
    slug: "subatomic-particle",
    say: "sub-uh-TOM-ik PAR-tih-kul",
    definition:
      "Any of the particles smaller than an atom that make it up — the proton, neutron, and electron.",
    week: 2,
  },
  {
    term: "Sublevel",
    slug: "sublevel",
    say: "SUB-lev-ul",
    ipa: "/ˈsʌblɛvəl/",
    definition:
      "A subdivision of an electron energy level, labeled s, p, d, or f; each sublevel holds a fixed maximum number of electrons (2, 6, 10, and 14, respectively).",
    week: 2,
  },
  {
    term: "Sublimation",
    slug: "sublimation",
    say: "sub-luh-MAY-shun",
    ipa: "/ˌsʌblɪˈmeɪʃən/",
    definition:
      "The change of state directly from a solid to a gas without passing through the liquid phase, as when dry ice turns to vapor.",
    week: 1,
  },
  {
    term: "Subscript",
    slug: "subscript",
    say: "SUB-skript",
    ipa: "/ˈsʌbskrɪpt/",
    definition:
      "The small lowered number after an element symbol in a formula that tells how many atoms of that element are present; no subscript means one atom.",
    week: 1,
  },
  {
    term: "Supersaturated solution",
    slug: "supersaturated-solution",
    say: "SOO-per-SACH-uh-ray-ted suh-LOO-shun",
    definition:
      "An unstable solution that temporarily holds more dissolved solute than its normal saturation limit, usually formed by carefully cooling a hot saturated solution.",
    week: 7,
  },
  {
    term: "Synthesis",
    slug: "synthesis",
    say: "SIN-thuh-sis",
    ipa: "/ˈsɪnθəsɪs/",
    definition:
      "A reaction in which two or more reactants combine to form a single product; the opposite of decomposition.",
    week: 5,
  },
  {
    term: "Tetrahedral",
    slug: "tetrahedral",
    say: "TET-ruh-HEE-drul",
    ipa: "/ˌtɛtrəˈhiːdrəl/",
    definition:
      "A VSEPR molecular shape with four bonds pointing to the corners of a tetrahedron around a central atom, giving bond angles of about 109.5°, as in methane.",
    week: 3,
  },
  {
    term: "Titration",
    slug: "titration",
    say: "ty-TRAY-shun",
    ipa: "/taɪˈtreɪʃən/",
    definition:
      "A laboratory technique that finds an unknown concentration by slowly adding a solution of known concentration until the reaction reaches its equivalence point.",
    week: 8,
  },
  {
    term: "Transition metals",
    slug: "transition-metals",
    say: "tran-ZISH-un MET-uhlz",
    ipa: "/trænˈzɪʃən ˈmɛtəlz/",
    definition:
      "The Groups 3-12 elements filling the center of the table (such as iron, copper, and gold): hard, dense, less-reactive metals, many of which form ions of more than one charge.",
    week: 1,
  },
  {
    term: "Trigonal planar",
    slug: "trigonal-planar",
    say: "TRIG-uh-nul PLAY-ner",
    ipa: "/ˈtrɪɡənəl ˈpleɪnər/",
    definition:
      "A VSEPR molecular shape with three bonds spread evenly in a flat triangle around a central atom, giving bond angles of 120°, as in SO3.",
    week: 3,
  },
  {
    term: "Trigonal pyramidal",
    slug: "trigonal-pyramidal",
    say: "TRIG-uh-nul puh-RAM-ih-dul",
    ipa: "/ˈtrɪɡənəl pɪˈræmɪdəl/",
    definition:
      "A VSEPR molecular shape in which three bonds and one lone pair around a central atom push the bonded atoms into a pyramid, as in ammonia.",
    week: 3,
  },
  {
    term: "Triple bond",
    slug: "triple-bond",
    say: "TRIP-ul BOND",
    definition:
      "A covalent bond in which two atoms share three pairs of electrons (six electrons total), as found between the carbons of an alkyne or in N2.",
    week: 3,
  },
  {
    term: "Unsaturated solution",
    slug: "unsaturated-solution",
    say: "un-SACH-uh-ray-ted suh-LOO-shun",
    definition:
      "A solution that contains less than the maximum amount of dissolved solute, so more solute could still dissolve.",
    week: 7,
  },
  {
    term: "Valence electrons",
    slug: "valence-electrons",
    say: "VAY-lunss ee-LEK-tronz",
    ipa: "/ˈveɪləns ɪˈlɛktrɒnz/",
    definition:
      "The electrons in an atom's outermost shell; their number determines how the atom bonds and is read from the element's group number.",
    week: 1,
  },
  {
    term: "VSEPR theory",
    slug: "vsepr-theory",
    say: "VES-per THEER-ee",
    ipa: "/ˈvɛspər ˈθɪəri/",
    definition:
      "Valence-Shell Electron-Pair Repulsion theory: the idea that electron pairs around a central atom spread out as far apart as possible, which predicts a molecule's three-dimensional shape.",
    week: 3,
  },
  {
    term: "Weighted average",
    slug: "weighted-average",
    say: "WAY-ted AV-rij",
    definition:
      "An average that accounts for how common each value is; an element's atomic mass is the weighted average of its isotopes' masses, scaled by their natural abundances.",
    week: 2,
  },
];
